import { createElement } from 'react';
import { FileCodeIcon, TextSelectIcon } from '@terra/ui-icons';
import type { ChatMentionsMenuItemType } from '@terra/ui-design-system';
import type { ObjectContextMentionData } from '@terra/shared-dto-chat-types';
import { truncateBodyForChat } from './httpFormatter';

function getSourceCodeFileName(filePath: string): string {
  return filePath.split('/').pop() || filePath;
}

type SourceCodeMentionLabels = {
  attachedExcerptInstruction: string;
  fileLabel: (location: string) => string;
  locationWithLines: (params: {
    path: string;
    lineStart: number;
    lineEnd: number;
  }) => string;
};

function formatSourceCodeRawText({
  filePath,
  repositoryName,
  content,
  lineStart,
  lineEnd,
  labels,
}: {
  filePath: string;
  repositoryName: string;
  content: string;
  lineStart?: number;
  lineEnd?: number;
  labels: SourceCodeMentionLabels;
}): string {
  const path = `${repositoryName}/${filePath}`;
  const location =
    lineStart !== undefined && lineEnd !== undefined
      ? labels.locationWithLines({ path, lineStart, lineEnd })
      : path;

  return [
    labels.attachedExcerptInstruction,
    labels.fileLabel(location),
    '',
    content,
  ].join('\n');
}

export function createSourceCodeObjectContextMention({
  fileId,
  filePath,
  repositoryName,
  content,
  selection,
  labels,
}: {
  fileId: string;
  filePath: string;
  repositoryName: string;
  content: string;
  selection?: {
    excerpt: string;
    lineStart?: number;
    lineEnd?: number;
  };
  labels: SourceCodeMentionLabels;
}): ChatMentionsMenuItemType {
  const fileName = getSourceCodeFileName(filePath);
  const hasSelection = Boolean(selection?.excerpt);
  const lineStart = selection?.lineStart;
  const lineEnd = selection?.lineEnd;
  const now = Date.now();
  const mentionLabel =
    hasSelection && lineStart !== undefined && lineEnd !== undefined
      ? `${fileName} (${lineStart}-${lineEnd})`
      : fileName;
  const sourceContent = hasSelection
    ? (selection?.excerpt ?? content)
    : content;
  const truncatedContent = truncateBodyForChat(sourceContent) ?? sourceContent;
  const rawText = formatSourceCodeRawText({
    filePath,
    repositoryName,
    content: truncatedContent,
    lineStart: hasSelection ? lineStart : undefined,
    lineEnd: hasSelection ? lineEnd : undefined,
    labels,
  });
  const objectContext: ObjectContextMentionData = {
    objectKind: 'SourceCode',
    objectId: fileId,
    objectLabel: fileName,
    selection: hasSelection
      ? {
          field: 'content',
          lineStart,
          lineEnd,
          excerpt: truncatedContent,
        }
      : undefined,
    rawText,
  };

  return {
    id: `object-context-SourceCode-${fileId}-${now}`,
    label: mentionLabel,
    icon: createElement(hasSelection ? TextSelectIcon : FileCodeIcon, {
      size: 12,
    }),
    type: 'resource',
    mentionEntity: {
      entityType: 'ObjectContext',
      entityId: `SourceCode:${fileId}:${now}`,
      label: mentionLabel,
      extraData: {
        objectContext,
      },
    },
  };
}
